<template>
  <main-dialog
    :visible="show"
    @hidden="$emit('close')"
    title="EXIF Data"
    size="lg"
    hide-footer
  >
    <div v-if="loading" class="text-center py-4">
      <b-spinner variant="primary"></b-spinner>
      <p class="mt-2">Loading EXIF data...</p>
    </div>
    
    <div v-else-if="error" class="alert alert-danger">
      <i class="fa fa-exclamation-triangle"></i>
      {{ error }}
    </div>
    
    <div v-else-if="exifData">
      <div class="exif-header mb-3">
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <strong>File:</strong> {{ file.name }}
          </div>
          <div>
            <b-badge variant="primary">{{ Object.keys(exifData).length }} fields</b-badge>
          </div>
        </div>
      </div>
      
      <!-- Camera Info Section -->
      <div v-if="cameraInfo.length > 0" class="exif-section mb-3">
        <h6 class="exif-section-title">
          <i class="fa fa-camera"></i> Camera Information
        </h6>
        <table class="exif-table">
          <thead>
            <tr>
              <th class="field-column">Field</th>
              <th class="value-column">Value</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in cameraInfo" :key="item.field">
              <td class="field-column"><strong>{{ item.field }}</strong></td>
              <td class="value-column">
                <code v-if="typeof item.value === 'string'">{{ item.value }}</code>
                <code v-else>{{ formatValue(item.value) }}</code>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      
      <!-- Exposure Info Section -->
      <div v-if="exposureInfo.length > 0" class="exif-section mb-3">
        <h6 class="exif-section-title">
          <i class="fa fa-sun-o"></i> Exposure Settings
        </h6>
        <table class="exif-table">
          <thead>
            <tr>
              <th class="field-column">Field</th>
              <th class="value-column">Value</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in exposureInfo" :key="item.field">
              <td class="field-column"><strong>{{ item.field }}</strong></td>
              <td class="value-column">
                <code v-if="typeof item.value === 'string'">{{ item.value }}</code>
                <code v-else>{{ formatValue(item.value) }}</code>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      
      <!-- Image Info Section -->
      <div v-if="imageInfo.length > 0" class="exif-section mb-3">
        <h6 class="exif-section-title">
          <i class="fa fa-image"></i> Image Properties
        </h6>
        <table class="exif-table">
          <thead>
            <tr>
              <th class="field-column">Field</th>
              <th class="value-column">Value</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in imageInfo" :key="item.field">
              <td class="field-column"><strong>{{ item.field }}</strong></td>
              <td class="value-column">
                <code v-if="typeof item.value === 'string'">{{ item.value }}</code>
                <code v-else>{{ formatValue(item.value) }}</code>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      
      <!-- Other EXIF Data -->
      <div v-if="otherData.length > 0" class="exif-section">
        <h6 class="exif-section-title">
          <i class="fa fa-info-circle"></i> Other Data
        </h6>
        <table class="exif-table">
          <thead>
            <tr>
              <th class="field-column">Field</th>
              <th class="value-column">Value</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in otherData" :key="item.field">
              <td class="field-column"><strong>{{ item.field }}</strong></td>
              <td class="value-column">
                <code v-if="typeof item.value === 'string'">{{ item.value }}</code>
                <code v-else>{{ formatValue(item.value) }}</code>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </main-dialog>
</template>

<script>
export default {
  name: 'ImageExifModal',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    file: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      error: null,
      exifData: null,
      tableFields: [
        { key: 'field', label: 'Field', tdClass: 'field-column' },
        { key: 'value', label: 'Value', tdClass: 'value-column' }
      ],
      cameraFields: ['Make', 'Model', 'LensModel', 'Software'],
      exposureFields: [
        'ExposureTime', 'FNumber', 'ApertureValue', 'ISOSpeedRatings',
        'ExposureProgram', 'ExposureMode', 'ExposureBiasValue',
        'MeteringMode', 'Flash', 'WhiteBalance', 'FocalLength',
        'FocalLengthIn35mmFilm', 'ShutterSpeedValue', 'BrightnessValue'
      ],
      imageFields: [
        'PixelXDimension', 'PixelYDimension', 'Orientation',
        'ColorSpace', 'XResolution', 'YResolution', 'ResolutionUnit',
        'Contrast', 'Saturation', 'Sharpness'
      ]
    };
  },
  computed: {
    cameraInfo() {
      return this.getFieldsByCategory(this.cameraFields);
    },
    exposureInfo() {
      return this.getFieldsByCategory(this.exposureFields);
    },
    imageInfo() {
      return this.getFieldsByCategory(this.imageFields);
    },
    otherData() {
      if (!this.exifData) return [];
      
      const knownFields = [
        ...this.cameraFields,
        ...this.exposureFields,
        ...this.imageFields
      ];
      
      return Object.keys(this.exifData)
        .filter(key => !knownFields.includes(key))
        .map(key => ({
          field: key,
          value: this.exifData[key]
        }));
    }
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.loadExifData();
      } else {
        this.exifData = null;
        this.error = null;
      }
    }
  },
  methods: {
    async loadExifData() {
      this.loading = true;
      this.error = null;
      this.exifData = null;
      
      try {
        const response = await this.$mediaHttp.get(
          `/v3/dms/media/exif/${this.file.key}`
        );
        
        this.exifData = response.data;
      } catch (error) {
        console.error('Failed to load EXIF data:', error);
        
        if (error.response && error.response.status === 400) {
          this.$bvToast.toast('No EXIF data found in this image', {
            title: 'Info',
            variant: 'info'
          });
          this.$emit('close');
        } else {
          this.error = (error.response && error.response.data && error.response.data.message) || 'Failed to load EXIF data';
        }
      } finally {
        this.loading = false;
      }
    },
    
    getFieldsByCategory(fields) {
      if (!this.exifData) return [];
      
      return fields
        .filter(field => this.exifData[field] !== undefined)
        .map(field => ({
          field: field,
          value: this.exifData[field]
        }));
    },
    
    formatValue(value) {
      if (Array.isArray(value)) {
        if (value.length === 1) {
          return this.formatSingleValue(value[0]);
        }
        return value.map(v => this.formatSingleValue(v)).join(', ');
      }
      return this.formatSingleValue(value);
    },
    
    formatSingleValue(value) {
      if (typeof value === 'string' && value.includes('/')) {
        // Try to evaluate fraction
        const parts = value.split('/');
        if (parts.length === 2) {
          const num = parseFloat(parts[0]);
          const den = parseFloat(parts[1]);
          if (!isNaN(num) && !isNaN(den) && den !== 0) {
            const result = num / den;
            return `${value} (${result.toFixed(2)})`;
          }
        }
      }
      return String(value);
    }
  }
};
</script>

<style lang="scss">
.exif-header {
  padding: 12px;
  background: var(--form-bg);
  border-radius: 6px;
  color: var(--text-primary);
}

.exif-section {
  border: 1px solid var(--border-default);
  border-radius: 6px;
  padding: 12px;
  background: var(--bg-card);
}

.exif-section-title {
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--border-default);
  
  i {
    color: var(--primary-start);
    margin-right: 8px;
  }
}

.exif-table {
  width: 100%;
  font-size: 13px;
  margin-bottom: 0;
  border-collapse: collapse;
}

.exif-table thead th {
  color: var(--text-primary);
  background-color: var(--bg-card);
  border: 1px solid var(--border-default);
  padding: 8px 12px;
  text-align: left;
  font-weight: 600;
}

.exif-table tbody tr {
  background-color: var(--bg-card);
  border: 1px solid var(--border-default);
}

.exif-table tbody tr:nth-child(even) {
  background-color: var(--bg-card);
}

.exif-table tbody tr:hover {
  background-color: rgba(108, 99, 255, 0.1);
}

.exif-table tbody td {
  color: var(--text-primary);
  border: 1px solid var(--border-default);
  padding: 8px 12px;
}

.exif-table tbody tr:hover td {
  color: var(--text-primary);
}

.exif-table tbody tr:hover strong {
  color: var(--text-primary);
}

.exif-table tbody tr:hover code {
  color: var(--primary-start);
}

.exif-table .field-column {
  width: 35%;
  font-weight: 500;
}

.exif-table .field-column strong {
  color: var(--text-primary);
}

.exif-table .value-column {
  width: 65%;
  word-break: break-word;
}

.exif-table code {
  color: var(--primary-start);
  font-size: 12px;
  background: rgba(108, 99, 255, 0.1);
  padding: 2px 6px;
  border-radius: 3px;
  white-space: pre-wrap;
}
</style>
